import axios from "axios";
import type { CarrierPurchasingOption } from "@/api/carriers";
import { discoverySourceLabels } from "./discovery-sources";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  useDiscoverServicesAggregate,
  useApplyDiscoveryCandidates,
  type DiscoveryCandidate,
  type AggregateDiscoveryResult,
} from "@/api/carrier-services";
import { CheckCircle2, XCircle, Loader2, RefreshCw } from "lucide-react";

function discoveryError(error: Error) {
  if (
    axios.isAxiosError<{ error?: string; details?: { message?: string } }>(
      error
    )
  ) {
    return (
      error.response?.data.details?.message ??
      error.response?.data.error ??
      error.message
    );
  }
  return error.message;
}

interface DiscoverServicesDialogProps {
  open: boolean;
  onClose: () => void;
  carrierId: string;
  carrierName: string;
  channels: CarrierPurchasingOption[];
  carrierCode: string;
}

export function DiscoverServicesDialog({
  open,
  onClose,
  carrierId,
  carrierName,
  channels,
  carrierCode,
}: DiscoverServicesDialogProps) {
  const [result, setResult] = useState<AggregateDiscoveryResult | null>(null);
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());

  const discoverMutation = useDiscoverServicesAggregate({
    onSuccess: (data) => {
      setResult(data);
      // Auto-select new (non-existing) candidates
      setSelectedCodes(
        new Set(
          data.candidates.filter((c) => !c.alreadyExists).map((c) => c.code)
        )
      );
    },
  });

  const applyMutation = useApplyDiscoveryCandidates({
    onSuccess: (data) => {
      toast.success("Services applied", {
        description: `Created ${data.createdCount}, updated ${data.updatedCount}.`,
      });
      onClose();
    },
  });

  // Fire one discovery query when the dialog transitions to open. Pull the
  // stable `mutate` out of the mutation object so the effect's dep list is
  // honest — putting `discoverMutation` itself there would re-fire whenever
  // its internal `isPending` flips.
  const { mutate: triggerDiscovery } = discoverMutation;
  useEffect(() => {
    if (!open) {
      setResult(null);
      setSelectedCodes(new Set());
      return;
    }
    triggerDiscovery({ carrierId });
  }, [open, carrierId, triggerDiscovery]);

  const toggle = (code: string) => {
    setSelectedCodes((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const apply = () => {
    if (!result) return;
    const candidates = result.candidates
      .filter((c) => selectedCodes.has(c.code))
      .map((c) => ({
        code: c.code,
        name: c.name,
        estimatedDays: c.estimatedDays,
        sourceProviderIds: c.sourceProviderIds,
      }));
    if (candidates.length === 0) return;
    applyMutation.mutate({ carrierId, candidates });
  };

  const configuredLabels = discoverySourceLabels(channels);
  const sourceLabels = new Map(
    result?.channels.map((channel, index) => [
      channel.channelId,
      configuredLabels.get(channel.channelId) ??
        `${channel.providerName} — unlisted option ${index + 1}`,
    ]) ?? []
  );

  const retryDiscovery = () => {
    applyMutation.reset();
    setResult(null);
    setSelectedCodes(new Set());
    discoverMutation.mutate({ carrierId });
  };
  const failedCount =
    result?.channels.filter((channel) => channel.error).length ?? 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && !applyMutation.isPending && onClose()}
    >
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Discover services for {carrierName}</DialogTitle>
          <DialogDescription>
            Review services returned by the configured integrations before
            applying them. Applying selected services refreshes their name and
            sources, and enables them. Provider days seed new services only;
            existing delivery days stay configured until edited in Services.
            Package limits, dim factors, pricing overrides and purchasing-option
            restrictions are unchanged.
          </DialogDescription>
        </DialogHeader>

        {carrierCode.toLowerCase() === "chitchats" &&
          channels.some(
            (channel) =>
              channel.active && channel.apiProvider?.code === "chitchats"
          ) && (
            <p className="text-sm text-muted-foreground">
              ChitChats discovery uses sample shipments and saved service
              information, not a complete catalogue. Availability depends on the
              shipment. You can also add a known service manually.
            </p>
          )}
        <p className="text-sm text-muted-foreground">
          Discovery uses EZX’s integration accounts. Separately linked account
          credentials are not used. Source labels distinguish saved purchasing
          options, not accounts queried.
        </p>
        {/* Purchasing option results */}
        {discoverMutation.isPending && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            Querying providers…
          </div>
        )}

        {discoverMutation.isError && (
          <p role="alert" className="text-sm text-destructive">
            Discovery failed: {discoveryError(discoverMutation.error)}. Retry
            discovery or add a known service manually.
          </p>
        )}
        {result && (
          <>
            {failedCount > 0 && (
              <p role="alert" className="text-sm text-destructive">
                {failedCount === result.channels.length
                  ? "Discovery failed for every purchasing option. Retry or add a known service manually."
                  : "Some purchasing options failed. Results below are partial; review the errors before applying."}
              </p>
            )}
            <div className="space-y-1 border rounded-md p-3 bg-muted/30">
              {result.channels.map((p) => (
                <div
                  key={p.channelId}
                  className="flex items-center gap-2 text-sm"
                >
                  {p.error ? (
                    <XCircle className="h-4 w-4 text-destructive" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  )}
                  <span className="font-medium">
                    {sourceLabels.get(p.channelId)}
                  </span>
                  {p.error ? (
                    <span className="text-destructive">→ {p.error}</span>
                  ) : (
                    <span className="text-muted-foreground">
                      → {p.serviceCount} service
                      {p.serviceCount === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Candidates table — long catalogs (UPS, FedEx) routinely
                exceed viewport height. Scroll the table; pin header + footer. */}
            <div className="border rounded-md overflow-y-auto flex-1 min-h-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Provider days</TableHead>
                    <TableHead>Sources</TableHead>
                    <TableHead className="w-24">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.candidates.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-muted-foreground py-6"
                      >
                        {failedCount > 0
                          ? "No services available to apply from this attempt."
                          : "No services returned. Try again later or add a known service manually."}
                      </TableCell>
                    </TableRow>
                  )}
                  {result.candidates.map((c: DiscoveryCandidate) => (
                    <TableRow key={c.code}>
                      <TableCell>
                        <Checkbox
                          disabled={
                            applyMutation.isPending ||
                            discoverMutation.isPending
                          }
                          aria-label={`Select ${c.name}`}
                          checked={selectedCodes.has(c.code)}
                          onCheckedChange={() => toggle(c.code)}
                        />
                      </TableCell>
                      <TableCell className="font-mono">{c.code}</TableCell>
                      <TableCell>{c.name}</TableCell>
                      <TableCell className="text-right">
                        {c.estimatedDays ?? "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {c.sourceProviderIds.map((id) => (
                            <Badge
                              key={id}
                              variant="secondary"
                              className="text-xs"
                            >
                              {sourceLabels.get(id) ??
                                "Unavailable purchasing option"}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {c.alreadyExists ? (
                          <Badge variant="outline" className="text-xs">
                            existing
                          </Badge>
                        ) : (
                          <Badge className="text-xs">new</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {applyMutation.isError && (
          <p role="alert" className="text-sm text-destructive">
            Apply failed: {discoveryError(applyMutation.error)}. Your selection
            is kept; retry when ready.
          </p>
        )}
        <DialogFooter className="flex items-center justify-between gap-2">
          {(result || discoverMutation.isError) && (
            <Button
              variant="outline"
              size="sm"
              onClick={retryDiscovery}
              disabled={discoverMutation.isPending || applyMutation.isPending}
            >
              <RefreshCw
                className={`h-3 w-3 mr-1 ${discoverMutation.isPending ? "animate-spin" : ""}`}
              />
              {discoverMutation.isError ? "Retry discovery" : "Re-query"}
            </Button>
          )}
          <div className="flex-1" />
          <Button
            variant="outline"
            onClick={onClose}
            disabled={applyMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={apply}
            disabled={
              !result ||
              selectedCodes.size === 0 ||
              applyMutation.isPending ||
              discoverMutation.isPending
            }
          >
            {applyMutation.isPending
              ? "Applying…"
              : `Apply ${selectedCodes.size} selected`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
